import { MiraAvailabilityState, MiraPublishedMetric, MiraPublishedPeriod } from "@/data/models/Mira";
import {
  COLOR_MIRA_BAR,
  COLOR_MIRA_BAR_DARK,
  COLOR_MIRA_BAR_TRACK,
  COLOR_MIRA_BAR_TRACK_DARK,
  COLOR_MIRA_INSURANCE_DIRECT,
  COLOR_MIRA_INSURANCE_DIRECT_DARK,
  COLOR_MIRA_INSURANCE_INSURED,
  COLOR_MIRA_INSURANCE_INSURED_DARK,
  COLOR_MIRA_INSURANCE_NO_INFO,
  COLOR_MIRA_INSURANCE_NO_INFO_DARK,
  COLOR_MIRA_OUTCOME_1,
  COLOR_MIRA_OUTCOME_1_DARK,
  COLOR_MIRA_OUTCOME_2,
  COLOR_MIRA_OUTCOME_2_DARK,
} from "@/utils/colors";
import { MiraBar, MiraSegment, MiraTile, MiraViewContext, MiraViewDelta, formatPercent, medianMeasure, metricTitle, publishedMetric, spreadLine } from "@/utils/MiraDataQualityView";
import { MiraDryingContext, MiraFact, daysText, durationFormatter, durationTileTexts, durationUnit, inPreparation, neutralDelta } from "@/utils/MiraDryingView";
import { MiraValueFormatter } from "@/utils/MiraTrendChart";
import { belowRowFloor, casesText, unitText } from "@/utils/MiraCaseUnits";

/**
 * The Versicherungen screen's view model: what each tile, part, bar and fact shows, built from a
 * published period and its comparison period. Pure, like the other Mira view models.
 *
 * The figures cover the offer a client sends the insurer from IRES Project and what the insurer
 * answers (IRES Cloud `metrics/insurance.ts`). Decided by Taimoor on 2026-10-01, each pinned by a test:
 * - **INS-006's HyDry / conventional split is never shown.** Setting the two overruns side by side is
 *   the HyDry benefit comparison Trocknung withholds (DRY-004), by the back door; only the overall
 *   overrun is read.
 * - **"In Vorbereitung" names only figures that are being prepared** ({@link NEVER_IN_PREPARATION}).
 * - As on the other screens: changes are neutral, the mean leads with the median beside it, and only a
 *   published figure gets a tile.
 */

/**
 * Withheld figures the "In Vorbereitung" line must not name: INS-007 (Nachträge, Kürzungen, Rechnungen,
 * Reklamationen) has no data source anywhere in IRES, so listing it would promise a figure that cannot come.
 */
export const NEVER_IN_PREPARATION = ["MIRA-INS-007"];

/** The insurer's answers in display order, each with its colour (the validated outcome pair). */
const ANSWERS = [
  { key: "accepted", color: COLOR_MIRA_OUTCOME_1, dark: COLOR_MIRA_OUTCOME_1_DARK },
  { key: "rejected", color: COLOR_MIRA_OUTCOME_2, dark: COLOR_MIRA_OUTCOME_2_DARK },
];

function tile(
  id: string,
  icon: string,
  value: string,
  caption: string | null,
  delta: MiraViewDelta | null,
  ctx: MiraDryingContext,
  spread: string | null = null
): MiraTile {
  return { id, icon, title: metricTitle(id, ctx), value, caption, delta, status: null, statusLabel: "", spread };
}

/** Days with their sign always written, "+10,0 Tage" or "−1,2 Tage": the overrun can be either. */
function signedDays(days: number, ctx: MiraDryingContext): string {
  const rounded = Math.round(days * 10) / 10 || 0;
  const sign = rounded > 0 ? "+" : rounded < 0 ? "−" : "";
  return daysText(`${sign}${ctx.formatDecimal(Math.abs(rounded), 1)}`, ctx);
}

/** The Versicherungen KPI row: approval time, acceptance rate, and the drying against its approval. */
export function buildInsuranceTiles(
  period: MiraPublishedPeriod | null,
  comparison: MiraPublishedPeriod | null,
  ctx: MiraDryingContext
): MiraTile[] {
  const tiles: MiraTile[] = [];
  const days = (magnitude: string) => daysText(magnitude, ctx);
  const points = (magnitude: string) => ctx.t("mira.format.deltaPoints", [magnitude]);

  const add = (id: string, icon: string, build: (metric: MiraPublishedMetric, previous: MiraPublishedMetric | null) => MiraTile) => {
    if (period?.metrics[id]?.state !== MiraAvailabilityState.AVAILABLE) return;
    const metric = publishedMetric(period, id);
    // No tile without a value (Taimoor, 2026-10-02): what a company would unlock goes on its own page (D2).
    if (metric) tiles.push(build(metric, publishedMetric(comparison, id)));
  };

  add("MIRA-INS-004", "mdi-timer-sand", (metric, previous) => {
    const texts = durationTileTexts(metric, previous, ctx);
    return tile("MIRA-INS-004", "mdi-timer-sand", texts.value,
      ctx.t("mira.insurance.captions.approval", [texts.median, ctx.formatNumber(metric.basis ?? 0)]),
      texts.delta, ctx, texts.spread);
  });

  add("MIRA-INS-005", "mdi-check-decagram-outline", (metric, previous) =>
    tile("MIRA-INS-005", "mdi-check-decagram-outline", formatPercent(metric.value ?? 0, ctx),
      ctx.t("mira.insurance.captions.acceptance", [ctx.formatNumber(metric.breakdown?.accepted ?? 0), ctx.formatNumber(metric.basis ?? 0)]),
      neutralDelta((metric.value ?? 0) * 100, previous ? (previous.value ?? 0) * 100 : null, 1, points, ctx, false), ctx));

  // The overall overrun only: the published HyDry / conventional split is never read (see the header).
  add("MIRA-INS-006", "mdi-calendar-alert", (metric, previous) =>
    tile("MIRA-INS-006", "mdi-calendar-alert", signedDays(metric.value ?? 0, ctx),
      ctx.t("mira.insurance.captions.overrun", [
        medianMeasure(metric, (value) => signedDays(value, ctx), ctx),
        ctx.formatNumber(metric.basis ?? 0),
      ]),
      neutralDelta(metric.value, previous?.value, 1, days, ctx), ctx,
      spreadLine(metric.p15, metric.p85, (value) => signedDays(value, ctx), ctx)));

  // INS-008: the share of projects that are direct orders among those where the field is set. Only
  // published where IRES Cloud finds the field usable, so a document without it shows no tile.
  add("MIRA-INS-008", "mdi-file-sign", (metric, previous) =>
    tile("MIRA-INS-008", "mdi-file-sign", formatPercent(metric.value ?? 0, ctx),
      ctx.t("mira.insurance.captions.directOrders", [
        ctx.formatNumber(metric.breakdown?.direct ?? 0),
        ctx.formatNumber(metric.basis ?? 0),
      ]),
      neutralDelta((metric.value ?? 0) * 100, previous ? (previous.value ?? 0) * 100 : null, 1, points, ctx, false), ctx));

  return tiles;
}

/**
 * The formatter the Verlauf card uses for a Versicherungen figure: the approval time in days, the
 * acceptance rate as a share, the overrun as signed days. Neutral, like every figure on the screen.
 */
export function insuranceTrendFormatter(metricId: string, ctx: MiraDryingContext, period: MiraPublishedPeriod | null = null): MiraValueFormatter {
  switch (metricId) {
    case "MIRA-INS-004":
      // In the tile's unit: hours where its headline for the chosen period is under one day.
      return durationFormatter(durationUnit(publishedMetric(period, metricId)?.value), ctx);
    case "MIRA-INS-005":
      return (value) => formatPercent(value, ctx);
    case "MIRA-INS-006":
      return (value) => signedDays(value, ctx);
    // INS-008 is a share: its Verlauf is a line, like the acceptance rate.
    case "MIRA-INS-008":
      return (value) => formatPercent(value, ctx);
    default:
      return (value) => ctx.formatNumber(value);
  }
}

/** INS-005: the insurer's accepted and rejected answers as parts of one bar. */
export function buildAnswerSegments(period: MiraPublishedPeriod | null, ctx: MiraDryingContext): MiraSegment[] {
  const breakdown = publishedMetric(period, "MIRA-INS-005")?.breakdown ?? {};
  const total = ANSWERS.reduce((sum, answer) => sum + (breakdown[answer.key] ?? 0), 0);
  if (!total) return [];

  return ANSWERS.filter((answer) => (breakdown[answer.key] ?? 0) > 0).map((answer) => ({
    key: answer.key,
    label: ctx.t(`mira.insurance.parts.${answer.key}`),
    value: breakdown[answer.key] ?? 0,
    valueText: ctx.formatNumber(breakdown[answer.key] ?? 0),
    shareText: formatPercent((breakdown[answer.key] ?? 0) / total, ctx),
    color: ctx.dark ? answer.dark : answer.color,
  }));
}

/**
 * INS-004's counts beside the answers: answers entered with their offer (no approval time), counted
 * rather than averaged. Only where INS-004 is published; impossible dates only when there are any. An
 * offer still waiting is no longer counted at all (#2610040, "Mira is a look back"): only answered offers
 * on finished projects count, by the month of the answer.
 */
export function answerFacts(period: MiraPublishedPeriod | null, ctx: MiraDryingContext): MiraFact[] {
  const metric = publishedMetric(period, "MIRA-INS-004");
  if (!metric) return [];

  const breakdown = metric.breakdown ?? {};
  const facts = [
    { label: ctx.t("mira.insurance.facts.recordedTogether"), value: ctx.formatNumber(breakdown.recordedTogether ?? 0) },
  ];
  if (breakdown.implausibleDates) {
    facts.push({ label: ctx.t("mira.insurance.facts.implausibleDates"), value: ctx.formatNumber(breakdown.implausibleDates) });
  }
  return facts;
}

function shareBar(key: string, label: string, share: number, ctx: MiraDryingContext, countText: string | null = null): MiraBar {
  return {
    key,
    label,
    fraction: share,
    valueText: formatPercent(share, ctx),
    countText,
    color: ctx.dark ? COLOR_MIRA_BAR_DARK : COLOR_MIRA_BAR,
    trackColor: ctx.dark ? COLOR_MIRA_BAR_TRACK_DARK : COLOR_MIRA_BAR_TRACK,
    deltaText: null,
    deltaDirection: "flat",
    showDelta: false,
  };
}

/**
 * INS-006 as two shares on a fixed 0 to 100 % scale: dryings that ran past the approved days, and offers
 * approved for fewer days than requested (only where requested days were filled in).
 */
export function buildApprovalBars(period: MiraPublishedPeriod | null, ctx: MiraDryingContext): MiraBar[] {
  const metric = publishedMetric(period, "MIRA-INS-006");
  const breakdown = metric?.breakdown;
  if (!breakdown) return [];

  // Each share names its cases (S1): the compared dryings, and the offers with requested days.
  const compared = typeof metric?.basis === "number" ? casesText("MIRA-INS-006", metric.basis, ctx) : null;
  const bars = [shareBar("overApproved", ctx.t("mira.insurance.bars.overApproved"), breakdown.shareOverApproved ?? 0, ctx, compared)];
  if ((breakdown.withRequest ?? 0) > 0) {
    const cut = shareBar("cut", ctx.t("mira.insurance.bars.cut"), breakdown.shareCut ?? 0, ctx, unitText("offers", breakdown.withRequest, ctx));
    // Under 10 offers with requested days: "zu wenige" and no bar (Track #2610072 S1).
    bars.push(belowRowFloor(breakdown.withRequest) ? { ...cut, fraction: 0, valueText: ctx.t("mira.trend.tooFew") } : cut);
  }
  return bars;
}

/** INS-006's overrun and the dryings it could not compare, beneath its bars. */
export function approvalFacts(period: MiraPublishedPeriod | null, ctx: MiraDryingContext): MiraFact[] {
  const metric = publishedMetric(period, "MIRA-INS-006");
  if (!metric) return [];

  const breakdown = metric.breakdown ?? {};
  return [
    { label: ctx.t("mira.insurance.facts.meanOverrun"), value: signedDays(metric.value ?? 0, ctx) },
    { label: ctx.t("mira.insurance.facts.medianOverrun"), value: signedDays(metric.median ?? 0, ctx) },
    { label: ctx.t("mira.insurance.facts.noApprovedDays"), value: ctx.formatNumber(breakdown.noApprovedDays ?? 0) },
    { label: ctx.t("mira.insurance.facts.noActual"), value: ctx.formatNumber(breakdown.noActual ?? 0) },
  ];
}

// --- Versicherer (MIRA-INS-001): the insurer mix of the projects finished in the period, #2610009 ---

/** The reserved buckets INS-001's breakdown carries beside the four-letter insurer keys. */
export const MIRA_INSURER_UNIDENTIFIED_KEY = "__unidentified";
export const MIRA_INSURER_DIRECT_KEY = "__direct";
/** A name decided "not an insurer" in the IRES Admin Client (Track #2610040 P3): "Kein Versicherer". */
export const MIRA_INSURER_NONE_KEY = "__none";
/** An empty insurer field (Hans, Track #2610072 S2): "Keine Angabe", apart from "nicht identifiziert". */
export const MIRA_INSURER_EMPTY_KEY = "__empty";

/** The row key of the folded "Weitere Versicherer" bar: a sentinel, so it never collides with a key. */
export const MIRA_INSURER_OTHERS_KEY = "__others";

/** How many insurer keys get their own bar before the rest fold into "Weitere Versicherer". */
export const MIRA_INSURER_ROWS = 10;

/** The buckets of INS-001's breakdown that are not an insurer key. */
const RESERVED_INSURER_KEYS = [MIRA_INSURER_UNIDENTIFIED_KEY, MIRA_INSURER_EMPTY_KEY, MIRA_INSURER_NONE_KEY, MIRA_INSURER_DIRECT_KEY];

/**
 * INS-001's finished projects in the Versicherer card's three cases (Hans, #2610137, decided by Taimoor
 * 2026-10-30): **mit Versicherung**, an insurance case with an insurer named, identified or not (the
 * insurer keys and "nicht identifiziert"); **keine Angabe**, an insurance case with nothing usable, an
 * empty field or a name IRES decided is no insurer ("Kein Versicherer"); and **Direktauftrag**, which beats
 * a typed-in name. An insurance case is any project that is not a direct order. Each part's share of every
 * finished project, the empty parts kept in the legend.
 */
function insuranceCases(breakdown: Record<string, number>): { insured: number; noInfo: number; direct: number } {
  const count = (key: string) => Math.max(0, breakdown[key] ?? 0);
  const insurers = Object.entries(breakdown)
    .filter(([key]) => !RESERVED_INSURER_KEYS.includes(key))
    .reduce((sum, [, projects]) => sum + Math.max(0, projects), 0);
  return {
    insured: insurers + count(MIRA_INSURER_UNIDENTIFIED_KEY),
    noInfo: count(MIRA_INSURER_EMPTY_KEY) + count(MIRA_INSURER_NONE_KEY),
    direct: count(MIRA_INSURER_DIRECT_KEY),
  };
}

/** The Versicherer card's first bar (#2610137): mit Versicherung / keine Angabe / Direktauftrag. */
export function buildInsuranceCaseSegments(period: MiraPublishedPeriod | null, ctx: MiraViewContext): MiraSegment[] {
  const breakdown = publishedMetric(period, "MIRA-INS-001")?.breakdown;
  if (!breakdown) {
    return [];
  }
  const cases = insuranceCases(breakdown);
  const total = cases.insured + cases.noInfo + cases.direct;
  if (total <= 0) {
    return [];
  }
  const parts = [
    { key: "insured", count: cases.insured, color: COLOR_MIRA_INSURANCE_INSURED, dark: COLOR_MIRA_INSURANCE_INSURED_DARK },
    { key: "noInfo", count: cases.noInfo, color: COLOR_MIRA_INSURANCE_NO_INFO, dark: COLOR_MIRA_INSURANCE_NO_INFO_DARK },
    { key: "direct", count: cases.direct, color: COLOR_MIRA_INSURANCE_DIRECT, dark: COLOR_MIRA_INSURANCE_DIRECT_DARK },
  ];
  return parts.map((part) => ({
    key: part.key,
    label: ctx.t(`mira.insurance.insurer.cases.${part.key}`),
    value: part.count,
    valueText: ctx.formatNumber(part.count),
    shareText: formatPercent(part.count / total, ctx),
    color: ctx.dark ? part.dark : part.color,
  }));
}

/**
 * The heading above the insurer bars: over how many projects with an insurer named they are shares (#2610137).
 * Null where INS-001 has none.
 */
export function insuredProjectsText(period: MiraPublishedPeriod | null, ctx: MiraViewContext): string | null {
  const breakdown = publishedMetric(period, "MIRA-INS-001")?.breakdown;
  const insured = breakdown ? insuranceCases(breakdown).insured : 0;
  return insured > 0 ? ctx.t("mira.insurance.insurer.insurersHeading", [ctx.formatNumber(insured)]) : null;
}

/**
 * INS-001 as insurer bars over the projects **with an insurer named** (Hans, #2610137): the ten largest
 * insurers by project count, then one folded "Weitere Versicherer" row, then "nicht identifiziert". Each
 * bar's length is its share of those projects, so Direktauftrag and keine Angabe, which the card's first bar
 * shows, no longer crowd the insurers out; the value beside it is that share, percentage first, with the
 * project count after it (Hans, #2610112 X2).
 *
 * The insurer keys are MDIS's four-letter keys; their names ride on the document (`metric.labels`), so
 * Office never keeps a copy of the list. A key the document does not name has no readable label, so it
 * folds into "Weitere Versicherer" rather than showing a bare key. Empty where INS-001 is absent, not
 * published, or no project names an insurer.
 *
 * @param period - the chosen period, or null.
 * @param ctx - translation, number formatting and theme.
 * @returns {MiraBar[]} the insurer bars, or none.
 */
export function buildInsurerRows(period: MiraPublishedPeriod | null, ctx: MiraViewContext): MiraBar[] {
  const metric = publishedMetric(period, "MIRA-INS-001");
  const breakdown = metric?.breakdown;
  const total = breakdown ? insuranceCases(breakdown).insured : 0;
  if (!metric || !breakdown || total <= 0) {
    return [];
  }

  const labels = metric.labels ?? {};
  const hasLabel = (key: string): boolean => labels[key] !== undefined && labels[key] !== "";
  const bar = (key: string, label: string, count: number): MiraBar => ({
    key,
    label,
    fraction: count / total,
    valueText: formatPercent(count / total, ctx),
    countText: unitText("projects", count, ctx),
    color: ctx.dark ? COLOR_MIRA_BAR_DARK : COLOR_MIRA_BAR,
    trackColor: ctx.dark ? COLOR_MIRA_BAR_TRACK_DARK : COLOR_MIRA_BAR_TRACK,
    deltaText: null,
    deltaDirection: "flat",
    showDelta: false,
  });

  // The insurer keys with a project, split from the reserved buckets.
  const insurerEntries = Object.entries(breakdown).filter(([key, count]) => !RESERVED_INSURER_KEYS.includes(key) && count > 0);
  // A named key gets its own bar; an unnamed one folds into "Weitere Versicherer" before ranking.
  const named = insurerEntries.filter(([key]) => hasLabel(key));
  const unlabeledCount = insurerEntries
    .filter(([key]) => !hasLabel(key))
    .reduce((sum, [, count]) => sum + count, 0);

  named.sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  const shown = named.slice(0, MIRA_INSURER_ROWS);
  const rest = named.slice(MIRA_INSURER_ROWS);
  const othersCount = rest.reduce((sum, [, count]) => sum + count, 0) + unlabeledCount;

  const rows: MiraBar[] = shown.map(([key, count]) => bar(key, labels[key], count));
  if (othersCount > 0) {
    rows.push(bar(MIRA_INSURER_OTHERS_KEY, ctx.t("mira.insurance.insurer.others"), othersCount));
  }
  const unidentified = breakdown[MIRA_INSURER_UNIDENTIFIED_KEY] ?? 0;
  if (unidentified > 0) {
    rows.push(bar(MIRA_INSURER_UNIDENTIFIED_KEY, ctx.t("mira.insurance.insurer.unidentified"), unidentified));
  }
  return rows;
}

/** The withheld figures that are being prepared, by name: Trocknung's rule, without INS-007. */
export function insuranceInPreparation(period: MiraPublishedPeriod | null, ctx: MiraDryingContext): string[] {
  const kept: MiraPublishedPeriod | null = period && {
    ...period,
    metrics: Object.fromEntries(Object.entries(period.metrics).filter(([id]) => !NEVER_IN_PREPARATION.includes(id))),
  };
  return inPreparation(kept, ctx);
}
